/* eslint indent: 0 */


// Note:
//
// Before the Codesbane (Page Editor for Multiple Sections) update a
// subpage/section were the same thing. (You could only have one section in a
// subpage, and they were both referred to as "sections").
//
// After Codesbane, the UI and almost all of the code refers to "subpages" which
// each contain 0 or more "sections".
//
// However, in order to ensure consistent data in our analytics system the data
// which we tracked as section or section_display previously,
//   will remain named as such.
//
// UI / Code --> Analytics
//
// Subpage --> Section
// Section --> Subsection


import autobind from 'class-autobind';
import loglevel from 'loglevel';
import get from 'lodash/get';

import {
  LINK_TYPE_EXTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import CoreTracker from './core_tracker.js';

import { getReduxStore } from 'zoomforth_codesbane_viewer/lib/global_store.js';


import {
  getContentItemType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  getAssetForContent,
  getContentItemByUuid,
  getCurrentSubpage,
  getPrimaryMenuItemForSubpage
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  SUBPAGE_DISPLAY_ACTION_NAME
} from './const.js';

/**
  
  We define a function which returns the desired maximum time we
  are willing to go without any activity event, having been emitted.

  The more events we emit (i.e., the more granular the timeframes we consider
  in our analytics), the more accurate our data, however, we don't want
  to overwhelm our analytics system so we dont want to emit too many!

  Additionally, the granularity with which we care about "time on page" 
  decreases as a function of time from first event. 
  i.e., it's more important to know that a person spent between 0:10-0:20 
  on a page, than it is to know they spent between 3:10-3:20. As such,
  we emit more events right after page load.

  This function attempts to strike the right balance between those constraints
  as determined by guesstimates and manual tuning.

  @param timeSinceFirstEventMs the number of ms since first page event was emitted
  @returns Maximum interval for which it is acceptable to not have any activity 
    events emitted (while some page interaction is occurring.

*/
export const getInteractionHeartbeatMaxIntervalMs = (timeSinceFirstEventMs)=> {
  if (timeSinceFirstEventMs >= 120000) {
    // After 2 minutes on the page, we are always happy with an event interval of 30 secs
    return 30000;

  } else if (timeSinceFirstEventMs >= 30000) {
    return 15000;

  } else {
    return 5000;
  }
};


/*
  Convert the content type into more queryable content types
*/
const getNormalizedType = function(type_) {
  return {
    photo:'image',
    youtube:'video',
    video:'video',
    text:'text',
    link:'link',
    pdf:'pdf',
    ppt:'ppt',
    other:'other',
    html_package:'html_package',
    iframe_embed:'iframe_embed',
    form:'form'
  }[type_];
};

const serializeContentForDisplay = function(pageContentItem) {
  var type_ = getContentItemType(pageContentItem);
  return {
    uuid: pageContentItem.uuid,
    id: pageContentItem.id,
    'type': type_,
    'normalized_type': getNormalizedType(type_),
    title: pageContentItem.title,
    description: pageContentItem.caption,
    asset: {
      id: pageContentItem.asset_id || 0
    }
  };
};

const serializeContentForTile = function(pageContentItem) {
  var type_ = getContentItemType(pageContentItem);
  return {
    uuid: pageContentItem.uuid,
    id: pageContentItem.id,
    'type': type_,
    normalized_type: getNormalizedType(type_),
    caption: pageContentItem.tile_caption,
    asset: {
      id: pageContentItem.asset_id || 0
    }
  };
};


const getVideoEventData = (contentItem, videoEventName, videoEventData)=>
  ({
    ...videoEventData,
    event_name: videoEventName,
    asset_id: get(contentItem, 'asset_id')
  });

const serializeSubpage = function(subpage) {
  const { title } = getPrimaryMenuItemForSubpage(getReduxStore().getState(), subpage) || {};

  return {
    id: subpage.id,
    title: title || '(Deleted Subpage)',
    type: subpage.type
  };
};


const findContentByUuid = function(contentUuid) {
  return getContentItemByUuid(getReduxStore().getState(), parseInt(contentUuid));
};

const findAssetForContent = function(contentItem) {
  return getAssetForContent(getReduxStore().getState(), contentItem);
};


class PageActivitiesTracker extends CoreTracker {

  constructor(...args) {
    super(...args);
    autobind(this);
  }

  trackWithIncludedSubpage(data, options) {
    const state = getReduxStore().getState();
    const subpage = getCurrentSubpage(state);

    const dataWithSubpage = { 
      subpage: serializeSubpage(subpage), 
      ...data 
    };

    this.track(dataWithSubpage, options);
  }

  ////
  //// Page Loading & Rendering
  //// 



  handlePageLoadAndPageRender() {
    this.track({
      action: 'page_load',
      outcome: 'page_render'
    });
  }

  handlePageUnload() {

    this.trackWithIncludedSubpage({
      // yes, its odd these are same value in two keys but we're stuck with it.
      action: 'page_unload',
      outcome: 'page_unload'
    
    }, { isEventOnUnload: true });
  }

//   handlePageRender() {
// 
//     this.track({
//       action: null,
//       outcome: 'page_render'
//     });
//   }

  handlePageLoadAndDisplayTrackingSplash() {

    this.track({
      action: 'page_load',
      outcome: 'display_track_splash'
    });
  }

  handleSubmitTrackingSplashAndPageRender() {
    this.track({
      action: 'submit',
      obj:'track_splash',
      outcome: 'page_render'
    });
  }

  ////
  //// Page interaction for heartbeats
  ////

  /**
    To reduce event volume we throttle event
  */
  handleThrottledInteractionHeartbeat() {
    const maxIntervalMs = getInteractionHeartbeatMaxIntervalMs(this.getMsSinceFirstEvent());
    if (this.getMsSincePreviousEvent() >= maxIntervalMs) {
      this.track({
        action: 'interaction_heartbeat'
      });
    }
  }

  ////
  //// Menu & Section Navigation
  ////

  handleNavMenuItemClick(menuItem, isInDropdown=false) {

    const menuItemClickData = {
      action:'click',
      obj: 'nav_menu',
      menu_text: menuItem.title,
      submenu: !!menuItem.isSubmenuItem,
      dropdown: isInDropdown
    };

    if (menuItem.appAction.type === LINK_TYPE_EXTERNAL) {

      this.trackWithIncludedSubpage({
        ...menuItemClickData,
        outcome: 'follow_link',
        external_link: menuItem.appAction.url,
        target_blank: menuItem.appAction.target === '_blank'
      });

    }  else {
      this.trackWithIncludedSubpage(menuItemClickData);
    }

  }

  handlePdfSubpageDisplayed(contentItem) {
    var serializedContent = serializeContentForDisplay(contentItem);
    this.trackWithIncludedSubpage({
      action: SUBPAGE_DISPLAY_ACTION_NAME,
      displayed_content: serializedContent
    });
  }

  handleSubpageDisplayed() {
    this.trackWithIncludedSubpage({
      action: SUBPAGE_DISPLAY_ACTION_NAME
    });
  }

  handleContentScrolledTop() {
    this.trackWithIncludedSubpage({
      action: null,
      'outcome': 'scroll_to_loc',
      'scroll_target': 'top'
    });
  }

  handleContentScrolledToTile(contentUuid) {
    var anchorContent = findContentByUuid(contentUuid);
    var serializedContent = serializeContentForTile(anchorContent);

    this.trackWithIncludedSubpage({
      action: null,
      'outcome': 'scroll_to_tile',
      'scroll_target_tile': serializedContent
    });
  }

  handleOverlayDisplayed(contentUuid) {
    // We must wait until other document click events get processed before processing this
    var content = findContentByUuid(contentUuid);
    var serializedContent = serializeContentForDisplay(content);

    const outcome = {
      video: 'display_video',
      pdf: 'display_pdf',
      ppt: 'display_ppt',
      image: 'display_image',
      iframe_embed: 'display_iframe_embed',
      html_package: 'display_html_package',
      other: 'display_other'

    }[serializedContent.normalized_type];

    this.trackWithIncludedSubpage({
      action: null,
      obj: 'overlay_item',
      displayed_content: serializedContent,
      outcome
    });

  }

  ////
  //// Social Icons
  ////

  handleSocialIconClick(e) {
    var $link = $(e.currentTarget);
    var $wrapper = $link.closest('.header-social-icon-wrapper, .social-icon-wrapper, .mobile-social-icon-wrapper');

    var serviceName = $wrapper.attr('data-service-name');

    this.trackWithIncludedSubpage({
      action: 'click',
      obj: 'social_icon',
      service_name: serviceName,
      external_link: $link.attr('href'),
      target_blank: $link.attr('target') === '_blank',
      outcome: 'follow_link'
    });
  }

  ////
  //// Tile Clicking
  ////

  handleTileClick(e) {
    var $tileWrapper = $(e.currentTarget);
    var actionType = $tileWrapper.attr('action_type');
    var tileType = $tileWrapper.attr('tile_type');
    var contentUuid = $tileWrapper.attr('content_uuid');

    if (!actionType || actionType === 'no') {
      return;
    }

    var serializedContent = serializeContentForTile(findContentByUuid(contentUuid));

    const tileClickData = {
      tile_content: serializedContent,
      action: 'click',
      obj: 'tile'
    };

    var $a = $tileWrapper.closest('a.grid-tile-wrapper');

    if (actionType === 'link') { 

      if (tileType === 'other') {

        this.trackWithIncludedSubpage({
          ...tileClickData,
          outcome: 'download_asset',
          displayed_content: serializedContent
        });

      } else {
        this.trackWithIncludedSubpage({
          ...tileClickData,
          outcome: 'follow_link',
          external_link: $a.attr('href'),
          target_blank: $a.attr('target') === '_blank'
        });
      }
    }
  }


  handleTextLinkClick(e) {
    var $link = $(e.currentTarget);
    var $twrap = $link.closest('.tile-wrapper');
    var appearance;

    if ($link.hasClass('button-style')) {
      appearance = 'button';
    } else {
      appearance = 'hyperlink';
    }

    const textTileClickData = {
      action: 'click',
      obj: 'hyperlink',
      outcome: 'follow_link',
      external_link: $link.attr('href'),
      link_text: $link.text(),
      link_appearance: appearance,
      target_blank: $link.attr('target') === '_blank'
    };

    if ($twrap.length) {
      this.trackWithIncludedSubpage({
        ...textTileClickData,
        'tile_content': serializeContentForTile(findContentByUuid($twrap.attr('content_uuid')))
      });

    } else {
      this.trackWithIncludedSubpage(textTileClickData);
    }
  }

  ////
  //// Video Playlists
  ////

  /**

    Track when a visitor selects a video playlist item. Should only occur when
    a the item is actually selected by a human
  
  */
  handleVideoPlaylistItemSelectHuman(contentItem) {

    this.trackWithIncludedSubpage({
      action: 'select',
      obj: 'videoplaylist_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: 'display_video'
    });
  }

  handleVideoPlaylistSubpageDisplayed(contentItem) {
    var serializedContent = serializeContentForDisplay(contentItem);
    
    this.trackWithIncludedSubpage({
      action: SUBPAGE_DISPLAY_ACTION_NAME,
      obj: 'videoplaylist_item',
      displayed_content: serializedContent,
      // TODO: We should stop treating all displays as a "play", instead we wait for video to start
      outcome: 'display_video'
    });
  }

  _handleVideoPlaylistItemEvent(contentItem, videoEventName, videoEventData) {

    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'videoplaylist_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, videoEventName, videoEventData)
    });

  }

  handleVideoPlaylistItemEnded(contentItem, videoEventData) {
    // The past-tense of the verb "ended" matches the HTML5 event name tense
    this._handleVideoPlaylistItemEvent(contentItem, 'ended', videoEventData);
  }

  handleVideoPlaylistItemError(contentItem, videoEventData) {
    this._handleVideoPlaylistItemEvent(contentItem, 'error', videoEventData);
  }

  handleVideoPlaylistItemPause(contentItem, videoEventData) {
    this._handleVideoPlaylistItemEvent(contentItem, 'pause', videoEventData);
  }

  handleVideoPlaylistItemPlay(contentItem, videoEventData) {
    this._handleVideoPlaylistItemEvent(contentItem, 'play', videoEventData);
  }

  handleVideoPlaylistItemSeeked(contentItem, videoEventData) {
    // The past-tense of the verb "seeked" matches the HTML5 event name tense
    this._handleVideoPlaylistItemEvent(contentItem, 'seeked', videoEventData);
  }

  handleVideoPlaylistItemStart(contentItem, videoEventData) {    
    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'videoplaylist_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, 'start', videoEventData)
    });
  }

  handleVideoPlaylistItemTimeUpdate(contentItem, eventData) {
    this._handleVideoPlaylistItemEvent(contentItem, 'timeupdate', eventData);
  }

  ////
  //// Begin Slideshows
  ////

  handleSlideshowItemSelect(contentItem) {
    var serializedContent = serializeContentForDisplay(contentItem);

    this.trackWithIncludedSubpage({
      action: 'select',
      obj: 'slideshow_item',
      displayed_content: serializedContent,
      outcome: serializedContent.normalized_type === 'video' ? 'display_video': 'display_image'
    });
  }

  handleSlideshowSubpageDisplayed(contentItem) {
    var serializedContent = serializeContentForDisplay(contentItem);
    
    this.trackWithIncludedSubpage({
      action: SUBPAGE_DISPLAY_ACTION_NAME,
      displayed_content: serializedContent,
      obj: 'slideshow_item',
      outcome: 
        serializedContent.normalized_type === 'video' ? 
          'display_video'
        : 
          'display_image'
    });
  }

  _handleSlideshowVideoEvent(contentItem, videoEventName, videoEventData) {

    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'slideshow_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, videoEventName, videoEventData)
    });

  }

  handleSlideshowVideoEnded(contentItem, videoEventData) {
    // The past-tense of the verb "ended" matches the HTML5 event name tense
    this._handleSlideshowVideoEvent(contentItem, 'ended', videoEventData);
  }

  handleSlideshowVideoError(contentItem, videoEventData) {
    this._handleSlideshowVideoEvent(contentItem, 'error', videoEventData);
  }

  handleSlideshowVideoPause(contentItem, videoEventData) {
    this._handleSlideshowVideoEvent(contentItem, 'pause', videoEventData);
  }

  handleSlideshowVideoPlay(contentItem, videoEventData) {
    this._handleSlideshowVideoEvent(contentItem, 'play', videoEventData);
  }

  handleSlideshowVideoSeeked(contentItem, videoEventData) {
    // The past-tense of the verb "seeked" matches the HTML5 event name tense
    this._handleSlideshowVideoEvent(contentItem, 'seeked', videoEventData);
  }

  handleSlideshowVideoStart(contentItem, videoEventData) {    
    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'videoplaylist_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, 'start', videoEventData)
    });
  }

  handleSlideshowVideoTimeUpdate(contentItem, eventData) {
    this._handleSlideshowVideoEvent(contentItem, 'timeupdate', eventData);
  }

  ////
  //// Overlay video
  ////

  _handleOverlayVideoEvent(contentItem, videoEventName, videoEventData) {

    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'overlay_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, videoEventName, videoEventData)
    });

  }

  handleOverlayVideoEnded(contentItem, videoEventData) {
    // The past-tense of the verb "ended" matches the HTML5 event name tense
    this._handleOverlayVideoEvent(contentItem, 'ended', videoEventData);
  }

  handleOverlayVideoError(contentItem, videoEventData) {
    this._handleOverlayVideoEvent(contentItem, 'error', videoEventData);
  }

  handleOverlayVideoPause(contentItem, videoEventData) {
    this._handleOverlayVideoEvent(contentItem, 'pause', videoEventData);
  }

  handleOverlayVideoPlay(contentItem, videoEventData) {
    this._handleOverlayVideoEvent(contentItem, 'play', videoEventData);
  }

  handleOverlayVideoSeeked(contentItem, videoEventData) {
    // The past-tense of the verb "seeked" matches the HTML5 event name tense
    this._handleOverlayVideoEvent(contentItem, 'seeked', videoEventData);
  }

  handleOverlayVideoStart(contentItem, videoEventData) {    
    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'overlay_item',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, 'start', videoEventData)
    });
  }

  handleOverlayVideoTimeUpdate(contentItem, eventData) {
    this._handleOverlayVideoEvent(contentItem, 'timeupdate', eventData);
  }

  ////
  //// Inline Video
  ////

  _handleInlineVideoEvent(contentItem, videoEventName, videoEventData) {
    this.trackWithIncludedSubpage({
      action: null, // We dont know (and sorta dont care) what initiated this
      obj: 'tile',
      displayed_content: serializeContentForDisplay(contentItem),
      outcome: null,
      video: getVideoEventData(contentItem, videoEventName, videoEventData)
    });
  }

  handleInlineVideoEnded(contentItem, videoEventData) {
    // The past-tense of the verb "ended" matches the HTML5 event name tense
    this._handleInlineVideoEvent(contentItem, 'ended', videoEventData);
  }

  handleInlineVideoError(contentItem, videoEventData) {
    this._handleInlineVideoEvent(contentItem, 'error', videoEventData);
  }

  handleInlineVideoPause(contentItem, videoEventData) {
    this._handleInlineVideoEvent(contentItem, 'pause', videoEventData);
  }

  handleInlineVideoPlay(contentItem, videoEventData) {
    this._handleInlineVideoEvent(contentItem, 'play', videoEventData);
  }

  handleInlineVideoSeeked(contentItem, videoEventData) {
    // The past-tense of the verb "seeked" matches the HTML5 event name tense
    this._handleInlineVideoEvent(contentItem, 'seeked', videoEventData);
  }

  handleInlineVideoStart(contentItem, videoEventData) {
    this._handleInlineVideoEvent(contentItem, 'start', videoEventData);
  }

  handleInlineVideoTimeUpdate(contentItem, eventData) {
    this._handleInlineVideoEvent(contentItem, 'timeupdate', eventData);
  }


  ////
  //// Misc
  ////

  handleFormSubmit(contentUuid) {
    var serializedContent = serializeContentForTile(findContentByUuid(contentUuid));

    this.trackWithIncludedSubpage({
      tile_content: serializedContent,
      action: 'submit',
      obj: 'form'
    });

  }

  handlePPTDownloadClick(contentUuid) {
    var content = findContentByUuid(contentUuid);
    var asset = findAssetForContent(content);
    var serializedContent = {
      ...serializeContentForDisplay(content),
      title: asset.display_filename,
      description: 'Download File'
    };

    this.trackWithIncludedSubpage({
      // tile_content: serializedContent, # Tile is not relevant and actually wrong sometimes here
      displayed_content: serializedContent,
      action: 'click',
      obj: 'download_icon',
      outcome: 'download_ppt'
    });

  }

  handlePPTSlideDisplayed(contentUuid, slideNum) {
    var content = findContentByUuid(contentUuid);
    var asset = findAssetForContent(content);
    var serializedContent = {
      ...serializeContentForDisplay(content),
      title: asset.display_filename,
      description: `Slide ${slideNum}`
    };

    this.trackWithIncludedSubpage({
      displayed_content: serializedContent,
      action: 'ppt_slide_display',
      slide_num: slideNum,
      obj: 'ppt_slide',
      outcome: 'display_ppt_slide'
    });
  }
}

export default PageActivitiesTracker;
